interface Deferred { promise: Promise<void>; resolve: () => void; reject: (err: Error) => void }

function deferred(): Deferred {
    let resolve: () => void = () => undefined;
    let reject: (err: Error) => void = () => undefined;
    const promise = new Promise<void>((res, rej) => { resolve = res; reject = rej; });
    return { promise, resolve, reject };
}

/**
 * Serializes one async job (a context load, a view refresh) so runs never overlap.
 *  - Join(): share the run in flight, or start one.
 *  - Again(): the caller needs a run that starts AFTER now (e.g. after a write). While a run is in
 *    flight, exactly one follow-up is queued and shared by every Again() meanwhile.
 * The follow-up becomes the in-flight run synchronously, inside the settle of the run before it —
 * before any awaiter of that run resumes — so there is no tick in which a new call finds nothing
 * in flight and starts a second, overlapping run.
 */
export class OEPCoalescedRunner {
    private inflight: Promise<void> | null = null;
    private queued: Deferred | null = null;

    constructor(private readonly work: () => Promise<void>) {}

    Join(): Promise<void> {
        return this.inflight ?? this.launch(deferred());
    }

    Again(): Promise<void> {
        if (this.inflight === null) return this.launch(deferred());
        this.queued ??= deferred();
        return this.queued.promise;
    }

    private launch(d: Deferred): Promise<void> {
        this.inflight = d.promise;
        this.work().then(
            () => { this.settle(d.promise); d.resolve(); },
            (err: Error) => { this.settle(d.promise); d.reject(err); },
        );
        return d.promise;
    }

    /** Runs before the finished run's promise settles, so its awaiters already see the follow-up in flight. */
    private settle(finished: Promise<void>): void {
        if (this.inflight !== finished) return;
        const next = this.queued;
        this.queued = null;
        if (next) this.launch(next);
        else this.inflight = null;
    }
}
